import { FC, useCallback, useEffect, useState } from "react";

import {
  Alert,
  Box,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  getRegionMap,
  GetRegionMapResponse,
  listOrg,
  RubiOrgModel,
} from "@rubi/api-generated";
import { Link, useNavigate } from "react-router-dom";

import { Breadcrumbs, Crumb, DevPage, ErrorMsg, Section } from "./common";
import { RegionGeometryMap } from "./RegionGeometryMap";
import { onRowActivateKeyDown } from "./regionShared";

const VISIBLE_COLOR = "#1f6feb";
const HIDDEN_COLOR = "#d97706";

const regionMapStyle: google.maps.Data.StylingFunction = (feature) => {
  const color =
    feature.getProperty("display_on_services_map") === false
      ? HIDDEN_COLOR
      : VISIBLE_COLOR;
  return {
    fillColor: color,
    fillOpacity: 0.2,
    strokeColor: color,
    strokeWeight: 2,
  };
};

interface HoveredRegion {
  orgTitle: string;
  regionId: string;
  hidden: boolean;
}

/**
 * Every org's active Region (`GET /region_map`, `low` tier only — the
 * endpoint serves nothing else), optionally including orgs hidden from the
 * services map. Click a Region to open it.
 */
const ActiveRegionsMapSection: FC = () => {
  const navigate = useNavigate();

  const [includeHidden, setIncludeHidden] = useState(false);
  const [regionMap, setRegionMap] = useState<GetRegionMapResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hovered, setHovered] = useState<HoveredRegion | null>(null);

  useEffect(() => {
    // Drop a response that lands after the toggle has flipped again.
    let stale = false;
    setError(null);
    getRegionMap({ query: { include_hidden: includeHidden } }).then(
      (response) => {
        if (!stale) setRegionMap(response.data ?? null);
      },
      (e) => {
        if (!stale) {
          setError(e instanceof Error ? e.message : "Failed to load map");
        }
      },
    );
    return () => {
      stale = true;
    };
  }, [includeHidden]);

  const openRegion = useCallback(
    (feature: google.maps.Data.Feature) =>
      navigate(
        `/dev_admin/region/${feature.getProperty("rubi_org_id")}/region/${feature.getProperty("region_id")}`,
      ),
    [navigate],
  );

  const onHover = useCallback(
    (feature: google.maps.Data.Feature | null) =>
      setHovered(
        feature && {
          orgTitle: String(feature.getProperty("rubi_org_title")),
          regionId: String(feature.getProperty("region_id")),
          hidden: feature.getProperty("display_on_services_map") === false,
        },
      ),
    [],
  );

  return (
    <Section subtitle="Active Regions">
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={includeHidden}
                onChange={(e) => setIncludeHidden(e.target.checked)}
              />
            }
            label="Include orgs hidden from the services map"
          />
          <Typography variant="body2" color="text.secondary">
            {regionMap && `${regionMap.features.length} regions · `}
            low tier · click a region to open it
          </Typography>
        </Stack>
        <ErrorMsg msg={error} />
        {regionMap ? (
          <>
            <RegionGeometryMap
              geoJson={regionMap}
              height={480}
              featureStyle={regionMapStyle}
              onFeatureClick={openRegion}
              onFeatureHover={onHover}
            />
            <Typography variant="body2" sx={{ minHeight: "1.5em" }}>
              {hovered ? (
                <>
                  <strong>{hovered.orgTitle}</strong>{" "}
                  <span className="mono muted">{hovered.regionId}</span>
                  {hovered.hidden && (
                    <span style={{ color: HIDDEN_COLOR }}> · hidden</span>
                  )}
                </>
              ) : (
                <span className="muted">Hover a region to identify it.</span>
              )}
            </Typography>
          </>
        ) : (
          !error && <Alert severity="info">Loading map…</Alert>
        )}
      </Stack>
    </Section>
  );
};

const columns: GridColDef<RubiOrgModel>[] = [
  { field: "id", headerName: "ID", width: 300 },
  { field: "title", headerName: "Title", flex: 1, minWidth: 200 },
  { field: "short_title", headerName: "Short Title", width: 160 },
];

/**
 * Entry point for the Regions feature: pick an org, then manage its
 * GeoDataFiles/Subregions/Regions. Modeled on `DevOrgConfigListPage` minus
 * the bootstrap-a-new-org form, which isn't relevant here.
 */
export const DevRegionOrgListPage: FC = () => {
  const navigate = useNavigate();

  const [orgs, setOrgs] = useState<RubiOrgModel[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    listOrg({ throwOnError: false }).then((response) => {
      if (response.status === 200) {
        setOrgs(response.data ?? []);
        setError(null);
      } else {
        setError(`${response.status} ${response.error?.console_msg ?? ""}`);
      }
      setLoading(false);
    });
  }, []);

  const openOrg = (orgId: string) =>
    navigate(`/dev_admin/region/${orgId}/geo_data_file`);

  const filtered = orgs.filter((o) => {
    if (!filter.trim()) return true;
    const f = filter.toLowerCase();
    return (
      o.id.toLowerCase().includes(f) ||
      (o.title ?? "").toLowerCase().includes(f) ||
      (o.short_title ?? "").toLowerCase().includes(f)
    );
  });

  return (
    <DevPage
      title="Regions"
      breadcrumbs={
        <Breadcrumbs>
          <Crumb>
            <Link to="/">Home</Link>
          </Crumb>
          <Crumb>
            <Link to="/dev_admin">Developer and Administrator Utilities</Link>
          </Crumb>
          <Crumb>Regions</Crumb>
        </Breadcrumbs>
      }
    >
      <Section subtitle="Pick an org">
        <ErrorMsg msg={error} />
        <Box sx={{ mb: 1 }}>
          <input
            type="search"
            placeholder="filter by id or title"
            aria-label="Filter orgs by id or title"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            name="region-org-list-filter"
            autoComplete="off"
            spellCheck={false}
            autoCapitalize="off"
            data-1p-ignore="true"
            data-lpignore="true"
            data-form-type="other"
            data-bwignore="true"
            style={{ width: 320 }}
          />
        </Box>
        <div style={{ width: "100%" }}>
          <DataGrid
            rows={filtered}
            columns={columns}
            loading={loading}
            autoHeight
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            pageSizeOptions={[10, 25, 50]}
            onRowClick={(params) => openOrg(String(params.id))}
            onCellKeyDown={onRowActivateKeyDown(openOrg)}
            sx={{ cursor: "pointer" }}
          />
        </div>
      </Section>

      <ActiveRegionsMapSection />
    </DevPage>
  );
};
